import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Fuel, Camera, ArrowLeft, AlertCircle, CheckCircle2, Clock, Truck, MapPin } from "lucide-react";

/**
 * Driver Loadings — see assigned loading tasks and submit readings + slip photo.
 *
 * Two source flavours (assigned by admin):
 *   • private_point: driver captures empty weight, loaded weight, weighbridge
 *                    slip photo. Density is supplied by admin at assign time
 *                    (driver can override if the source discloses a fresh value).
 *   • petrol_pump:   driver captures pump start reading, pump end reading,
 *                    pump slip photo.
 *
 * On submit, backend computes litres and moves the loading into `submitted`
 * status. An admin then approves — only then is the bowser stock topped up.
 */

const SOURCE_LABELS = {
  private_point: "Private point (weighbridge)",
  petrol_pump: "Petrol pump",
  depot: "Depot",
};


export function DriverLoadingsList() {
  const { data, isLoading } = useQuery({
    queryKey: ["driver-loadings"],
    queryFn: () => api.get("/loadings/mine").then((r) => r.data.items || []),
    refetchInterval: 20000,
  });

  return (
    <div className="space-y-4" data-testid="driver-loadings-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Loading tasks</h1>
        <p className="text-sm text-muted-foreground">
          Pickups assigned to you · complete on-site, submit readings + slip, wait for approval.
        </p>
      </div>

      {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!isLoading && (!data || data.length === 0) && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No pending loading tasks. Dispatcher will assign one when needed.
          </CardContent>
        </Card>
      )}

      {(data || []).map((l) => (
        <Card key={l.id} data-testid={`driver-loading-${l.code}`}>
          <CardContent className="space-y-3 pt-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono text-sm font-bold">{l.code}</div>
                <div className="mt-0.5 text-lg font-black">{l.vendor_name || SOURCE_LABELS[l.source_type]}</div>
                <div className="text-xs text-muted-foreground">{SOURCE_LABELS[l.source_type]}</div>
              </div>
              <StatusBadge status={l.status} />
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Bowser</div>
                <div className="font-semibold">{l.bowser_code}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Expected</div>
                <div>{l.expected_litres ? `${Number(l.expected_litres).toFixed(0)} L` : "—"}</div>
              </div>
            </div>
            {l.rejected_reason && (
              <div className="rounded-md border border-status-cancelled/40 bg-status-cancelled/5 p-3 text-xs">
                <div className="flex items-start gap-2">
                  <AlertCircle size={14} className="mt-0.5 shrink-0 text-status-cancelled" />
                  <div>
                    <div className="font-bold text-status-cancelled">Last submission rejected — please resubmit</div>
                    <div className="mt-1 text-muted-foreground">{l.rejected_reason}</div>
                  </div>
                </div>
              </div>
            )}
            {l.notes && (
              <div className="rounded-md bg-muted/40 p-2 text-xs text-muted-foreground">
                <b>Instructions:</b> {l.notes}
              </div>
            )}
            {l.status === "assigned" && (
              <Link to={`/driver/loadings/${l.id}`}>
                <Button className="pill-btn w-full" data-testid={`btn-submit-loading-${l.code}`}>
                  <Fuel size={16} className="mr-2" /> Complete + submit
                </Button>
              </Link>
            )}
            {l.status === "submitted" && (
              <div className="flex items-center gap-2 rounded-md border border-yellow-500/40 bg-yellow-500/5 px-3 py-2 text-xs text-yellow-700">
                <Clock size={14} /> Waiting for admin approval · <span className="font-mono">{l.litres_loaded?.toFixed(0)} L</span>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


function StatusBadge({ status }) {
  const map = {
    assigned: { cls: "bg-status-scheduled/10 text-status-scheduled border-status-scheduled/30", label: "Pending" },
    submitted: { cls: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30", label: "Awaiting approval" },
    approved: { cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30", label: "Approved" },
    rejected: { cls: "bg-status-cancelled/10 text-status-cancelled border-status-cancelled/30", label: "Rejected" },
  };
  const s = map[status] || map.assigned;
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${s.cls}`}>
      {s.label}
    </span>
  );
}


export function DriverLoadingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: l, isLoading } = useQuery({
    queryKey: ["driver-loading", id],
    queryFn: () => api.get(`/loadings/${id}`).then((r) => r.data),
  });

  const [form, setForm] = useState({
    empty_weight_kg: 0,
    loaded_weight_kg: 0,
    density_kg_per_litre: 0,
    pump_start_reading: 0,
    pump_end_reading: 0,
    slip_photo_url: "",
    batch_no: "",
    seal_no: "",
    temperature_c: 0,
    notes: "",
  });
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Prefill density from assign-time value once loading arrives
  React.useEffect(() => {
    if (l?.density_kg_per_litre && !form.density_kg_per_litre) {
      setForm((f) => ({ ...f, density_kg_per_litre: l.density_kg_per_litre }));
    }
  }, [l?.density_kg_per_litre, form.density_kg_per_litre]);

  const submitMut = useMutation({
    mutationFn: () => {
      const payload = {
        slip_photo_url: form.slip_photo_url,
        batch_no: form.batch_no,
        seal_no: form.seal_no,
        temperature_c: form.temperature_c || null,
        notes: form.notes,
      };
      if (l.source_type === "private_point") {
        payload.empty_weight_kg = Number(form.empty_weight_kg);
        payload.loaded_weight_kg = Number(form.loaded_weight_kg);
        payload.density_kg_per_litre = Number(form.density_kg_per_litre);
      } else {
        payload.pump_start_reading = Number(form.pump_start_reading);
        payload.pump_end_reading = Number(form.pump_end_reading);
      }
      return api.post(`/loadings/${id}/submit`, payload);
    },
    onSuccess: () => {
      toast.success("Submitted for approval");
      qc.invalidateQueries({ queryKey: ["driver-loadings"] });
      nav("/driver/loadings");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Submit failed"),
  });

  // Photo upload — placeholder that opens the device camera on mobile and
  // returns a local blob URL. Real deployment would push to object-storage
  // and use the returned URL. Kept intentionally simple for now.
  const onPhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setVal("slip_photo_url", reader.result);
    reader.readAsDataURL(file);
  };

  if (isLoading || !l) return <div className="p-4 text-sm text-muted-foreground">Loading…</div>;

  // Compute preview litres so driver can eyeball before submitting
  let previewLitres = 0;
  if (l.source_type === "private_point") {
    const net = Number(form.loaded_weight_kg) - Number(form.empty_weight_kg);
    const dens = Number(form.density_kg_per_litre) || 1;
    if (net > 0 && dens > 0) previewLitres = net / dens;
  } else {
    previewLitres = Math.max(0, Number(form.pump_end_reading) - Number(form.pump_start_reading));
  }

  const canSubmit =
    !!form.slip_photo_url &&
    (l.source_type === "private_point"
      ? form.empty_weight_kg > 0 && form.loaded_weight_kg > form.empty_weight_kg && form.density_kg_per_litre > 0
      : form.pump_start_reading >= 0 && form.pump_end_reading > form.pump_start_reading);

  return (
    <div className="space-y-4" data-testid="driver-loading-detail-page">
      <Button variant="ghost" onClick={() => nav("/driver/loadings")}>
        <ArrowLeft size={16} className="mr-2" /> Back to loadings
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck size={16} className="text-primary" /> {l.code}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Source</div>
              <div className="font-semibold">{SOURCE_LABELS[l.source_type]}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Bowser</div>
              <div className="font-semibold">{l.bowser_code}</div>
            </div>
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Vendor / pump</div>
              <div className="flex items-center gap-1 font-semibold"><MapPin size={12} /> {l.vendor_name || "—"}</div>
            </div>
          </div>
          {l.rejected_reason && (
            <div className="rounded-md border border-status-cancelled/40 bg-status-cancelled/5 p-3 text-xs">
              <b className="text-status-cancelled">Previous submission rejected:</b>
              <div className="mt-1 text-muted-foreground">{l.rejected_reason}</div>
            </div>
          )}
          {l.notes && (
            <div className="rounded-md bg-muted/40 p-2 text-xs text-muted-foreground">
              <b>Instructions:</b> {l.notes}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Enter readings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {l.source_type === "private_point" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Empty weight (kg) <span className="text-status-cancelled">*</span></Label>
                  <Input type="number" step="0.1" value={form.empty_weight_kg || ""}
                         onChange={(e) => setVal("empty_weight_kg", Number(e.target.value))}
                         placeholder="Bowser tare from slip" data-testid="drv-loading-empty-kg" />
                </div>
                <div>
                  <Label>Loaded weight (kg) <span className="text-status-cancelled">*</span></Label>
                  <Input type="number" step="0.1" value={form.loaded_weight_kg || ""}
                         onChange={(e) => setVal("loaded_weight_kg", Number(e.target.value))}
                         placeholder="Gross weight from slip" data-testid="drv-loading-loaded-kg" />
                </div>
              </div>
              <div>
                <Label>Density (kg/L) <span className="text-status-cancelled">*</span></Label>
                <Input type="number" step="0.001" value={form.density_kg_per_litre || ""}
                       onChange={(e) => setVal("density_kg_per_litre", Number(e.target.value))}
                       placeholder="e.g. 0.832" data-testid="drv-loading-density" />
                <div className="mt-1 text-xs text-muted-foreground">
                  Preset by dispatcher; adjust only if the source discloses a different value.
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Pump START reading <span className="text-status-cancelled">*</span></Label>
                <Input type="number" step="0.01" value={form.pump_start_reading || ""}
                       onChange={(e) => setVal("pump_start_reading", Number(e.target.value))}
                       placeholder="Meter reading BEFORE" data-testid="drv-loading-pump-start" />
              </div>
              <div>
                <Label>Pump END reading <span className="text-status-cancelled">*</span></Label>
                <Input type="number" step="0.01" value={form.pump_end_reading || ""}
                       onChange={(e) => setVal("pump_end_reading", Number(e.target.value))}
                       placeholder="Meter reading AFTER" data-testid="drv-loading-pump-end" />
              </div>
            </div>
          )}

          <div className="rounded-md bg-primary/5 border border-primary/30 p-3 text-sm">
            <div className="flex items-center gap-2 font-bold">
              <Fuel size={14} /> Litres loaded (preview):
              <span className="text-primary text-lg" data-testid="drv-loading-preview-litres">{previewLitres.toFixed(1)} L</span>
            </div>
          </div>

          <div>
            <Label>Slip photo <span className="text-status-cancelled">*</span></Label>
            <div className="mt-1 flex items-center gap-3">
              <label className="flex-1 cursor-pointer rounded-md border border-dashed border-border/60 px-3 py-3 text-center text-sm hover:border-primary hover:bg-primary/5">
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhotoPick} data-testid="drv-loading-photo" />
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <Camera size={16} /> {form.slip_photo_url ? "Retake photo" : "Take slip photo"}
                </div>
              </label>
              {form.slip_photo_url && (
                <img src={form.slip_photo_url} alt="slip" className="h-16 w-16 rounded-md border border-border/60 object-cover" />
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Batch # (optional)</Label>
              <Input value={form.batch_no} onChange={(e) => setVal("batch_no", e.target.value)} data-testid="drv-loading-batch" />
            </div>
            <div>
              <Label>Seal # (optional)</Label>
              <Input value={form.seal_no} onChange={(e) => setVal("seal_no", e.target.value)} data-testid="drv-loading-seal" />
            </div>
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setVal("notes", e.target.value)} placeholder="Anything the office should know" />
          </div>

          <Button
            size="lg"
            className="pill-btn w-full"
            onClick={() => submitMut.mutate()}
            disabled={!canSubmit || submitMut.isPending}
            data-testid="btn-drv-loading-submit"
          >
            <CheckCircle2 size={16} className="mr-2" />
            {submitMut.isPending ? "Submitting…" : "Submit for approval"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}


// Default export = the list; the detail page is a named export routed separately.
export default DriverLoadingsList;
